import { toggleDebugMode } from '../utils/debug.js';
import { audioManager } from './AudioManager.js';
import { TEAM_CONFIG } from '../config.js';

export class InputManager {
  constructor(canvas, onSpawn, onRendererToggle = null, onPauseToggle = null) {
    this.canvas = canvas;
    this.onSpawn = onSpawn;
    this.onRendererToggle = onRendererToggle;
    this.onPauseToggle = onPauseToggle;

    // Track pointer state for drag spawning
    this.isPointerDown = false;
    this.ignorePointer = false;
    
    // Spawn type system
    this.spawnTypes = [
      // Team soldiers
      ...TEAM_CONFIG.TEAMS.map(team => ({ type: 'soldier', team: team })),
      // Resources
      { type: 'metal', name: 'Metal' },
      { type: 'crystal', name: 'Crystal' },
      { type: 'nebula', name: 'Nebula' }
    ];
    
    // Load saved spawn type from localStorage
    const savedSpawnIndex = localStorage.getItem('soulforge_spawnTypeIndex');
    this.currentSpawnIndex = savedSpawnIndex ? parseInt(savedSpawnIndex, 10) : 0;
    
    // Validate the saved index
    if (this.currentSpawnIndex < 0 || this.currentSpawnIndex >= this.spawnTypes.length) {
      this.currentSpawnIndex = 0;
    }

    // Bind handlers so they can be removed later
    this.boundPointerDown = (e) => this.handlePointerDown(e);
    this.boundPointerMove = (e) => this.handlePointerMove(e);
    this.boundPointerUp = (e) => this.handlePointerUp(e);
    this.boundKeyDown = (e) => this.handleKeyDown(e);
    this.boundResize = () => this.handleResize();
    this.boundTouchStart = (e) => this.handleTouchStart(e);
    this.boundTouchEnd = (e) => this.handleTouchEnd(e);
    this.boundRendererClick = (e) => this.handleRendererClick(e);
    this.boundSpawnClick = (e) => this.handleSpawnClick(e);
    this.boundOptionsClick = (e) => this.handleOptionsClick(e);
    this.boundStatsClick = (e) => this.handleStatsClick(e);
    this.boundDebugClick = (e) => this.handleDebugClick(e);
    this.boundVersionClick = (e) => this.handleVersionClick(e);

    // Prevent default gestures on touch devices
    this.canvas.style.touchAction = 'none';

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Pointer events for click and drag spawning
    this.canvas.addEventListener('pointerdown', this.boundPointerDown);
    this.canvas.addEventListener('pointermove', this.boundPointerMove);
    window.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('pointercancel', this.boundPointerUp);

    // Touch events for 3 finger debug toggle
    this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.canvas.addEventListener('touchend', this.boundTouchEnd);

    // Handle keyboard input
    document.addEventListener('keydown', this.boundKeyDown);

    // Handle window resize
    window.addEventListener('resize', this.boundResize);
    
    // Handle bottom control buttons
    const optionsBtn = document.getElementById('optionsBtn');
    const statsBtn = document.getElementById('statsBtn');
    const debugBtn = document.getElementById('debugBtn');
    const versionBtn = document.getElementById('versionBtn');
    
    if (optionsBtn) optionsBtn.addEventListener('click', this.boundOptionsClick);
    if (statsBtn) statsBtn.addEventListener('click', this.boundStatsClick);
    if (debugBtn) debugBtn.addEventListener('click', this.boundDebugClick);
    if (versionBtn) versionBtn.addEventListener('click', this.boundVersionClick);
    
    // Restore UI visibility state
    const uiHidden = localStorage.getItem('soulforge_uiHidden') === 'true';
    if (uiHidden) {
      // Hide UI on startup if it was hidden
      setTimeout(() => this.toggleUI(), 100);
    }
    
    // Restore individual panel visibility states
    setTimeout(() => {
      // Stats window will restore its own visibility from StatsWindow class
      
      // Restore debug panel visibility
      const debugVisible = localStorage.getItem('soulforge_debugVisible') === 'true';
      if (debugVisible) {
        toggleDebugMode(); // This will turn on debug mode if it was saved as on
      }
    }, 150); // Slightly after UI toggle to ensure proper state
  }

  handlePointerDown(e) {
    if (this.ignorePointer) return;
    e.preventDefault();
    this.isPointerDown = true;
    this.spawnFromEvent(e);
  }

  handlePointerMove(e) {
    if (!this.isPointerDown || this.ignorePointer) return;
    e.preventDefault();
    this.spawnFromEvent(e);
  }

  handlePointerUp() {
    this.isPointerDown = false;
    this.ignorePointer = false;
  }

  handleTouchStart(e) {
    if (e.touches.length === 3) {
      // Three finger tap toggles debug mode
      toggleDebugMode();
      this.ignorePointer = true; // Ignore resulting pointer events
    } else if (e.touches.length === 2) {
      // Two finger tap toggles UI
      this.toggleUI();
      this.ignorePointer = true; // Ignore resulting pointer events
    }
  }

  handleTouchEnd(e) {
    if (e.touches.length === 0) {
      this.ignorePointer = false;
    }
  }

  spawnFromEvent(e) {
    // Initialize audio on first user interaction
    audioManager.init();
    
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (this.onSpawn) {
      const spawnType = this.spawnTypes[this.currentSpawnIndex];
      this.onSpawn(x, y, spawnType);
    }
  }

  handleKeyDown(e) {
    const key = e.key.toLowerCase();
    
    if (key === 'd') {
      toggleDebugMode();
    } else if (key === 't') {
      this.cycleSpawnType();
    } else if (key === 'r') {
      // Toggle renderer
      if (this.onRendererToggle) {
        this.onRendererToggle();
      }
    } else if (key === 'u') {
      // Toggle all UI visibility
      this.toggleUI();
    } else if (key === 'c') {
      // Toggle configuration window
      if (window.configWindow) {
        window.configWindow.toggle();
      }
    } else if (key === 'v') {
      // Toggle version notes window
      if (window.versionNotesWindow) {
        window.versionNotesWindow.toggle();
      }
    } else if (key === ' ') {
      // Spacebar - toggle pause
      e.preventDefault(); // Prevent page scroll
      if (this.onPauseToggle) {
        this.onPauseToggle();
      }
    }
  }
  
  toggleStats() {
    if (window.statsWindow) {
      window.statsWindow.toggle();
    }
  }
  
  toggleUI() {
    // Get all UI elements
    const uiElements = [
      window.statsWindow?.container,
      document.getElementById('rendererDisplay'),
      document.getElementById('spawnTypeDisplay'),
      document.getElementById('fpsDisplay'),
      document.getElementById('optionsBtn'),
      document.getElementById('statsBtn'),
      document.getElementById('debugBtn'),
      document.getElementById('versionBtn'),
      document.getElementById('version')
    ].filter(Boolean); // Remove any null/undefined elements
    
    // Check if any UI is currently visible
    const anyVisible = uiElements.some(el => el && el.style.display !== 'none');
    
    // Toggle all UI elements
    uiElements.forEach(el => {
      if (el) {
        el.style.display = anyVisible ? 'none' : (el.style.display === 'none' ? '' : el.style.display);
      }
    });
    
    // Store UI visibility state
    localStorage.setItem('soulforge_uiHidden', anyVisible ? 'true' : 'false');
  }
  
  cycleSpawnType() {
    // Cycle through spawn types
    this.currentSpawnIndex = (this.currentSpawnIndex + 1) % this.spawnTypes.length;
    
    // Save to localStorage
    localStorage.setItem('soulforge_spawnTypeIndex', this.currentSpawnIndex.toString());
    
    // Update display immediately
    if (window.game && window.game.updateStats) {
      window.game.updateStats();
    }
  }
  
  handleRendererClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    if (this.onRendererToggle) {
      this.onRendererToggle();
    }
  }
  
  handleSpawnClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    this.cycleSpawnType();
  }
  
  handleOptionsClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    // Trigger the 'C' key event for config UI
    const event = new KeyboardEvent('keydown', { key: 'c' });
    document.dispatchEvent(event);
  }
  
  handleStatsClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    this.toggleStats();
  }
  
  handleDebugClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    // Toggle stats panel visibility (which includes debug)
    this.toggleStats();
  }
  
  handleVersionClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Initialize audio on first user interaction
    audioManager.init();
    
    // Toggle version notes window
    if (window.versionNotesWindow) {
      window.versionNotesWindow.toggle();
    }
  }
  
  getCurrentSpawnType() {
    return this.spawnTypes[this.currentSpawnIndex];
  }

  handleResize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }
  
  bindUIElements(rendererDiv, spawnDiv) {
    // Bind click handlers to UI elements
    if (rendererDiv) {
      rendererDiv.addEventListener('click', this.boundRendererClick);
      this.rendererDiv = rendererDiv;
    }
    
    if (spawnDiv) {
      spawnDiv.addEventListener('click', this.boundSpawnClick);
      this.spawnDiv = spawnDiv;
    }
  }

  destroy() {
    // Clean up event listeners
    this.canvas.removeEventListener('pointerdown', this.boundPointerDown);
    this.canvas.removeEventListener('pointermove', this.boundPointerMove);
    window.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('pointercancel', this.boundPointerUp);
    this.canvas.removeEventListener('touchstart', this.boundTouchStart);
    this.canvas.removeEventListener('touchend', this.boundTouchEnd);
    document.removeEventListener('keydown', this.boundKeyDown);
    window.removeEventListener('resize', this.boundResize);
    
    // Clean up button listeners
    const optionsBtn = document.getElementById('optionsBtn');
    const statsBtn = document.getElementById('statsBtn');
    const debugBtn = document.getElementById('debugBtn');
    
    if (optionsBtn) optionsBtn.removeEventListener('click', this.boundOptionsClick);
    if (statsBtn) statsBtn.removeEventListener('click', this.boundStatsClick);
    if (debugBtn) debugBtn.removeEventListener('click', this.boundDebugClick);
    
    // Clean up UI element listeners
    if (this.rendererDiv) {
      this.rendererDiv.removeEventListener('click', this.boundRendererClick);
    }
    
    if (this.spawnDiv) {
      this.spawnDiv.removeEventListener('click', this.boundSpawnClick);
    }
  }
}